import { Box, Typography } from "@mui/material"
import React, { useEffect, useState } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { User } from "./models/User"
import Cookies from "js-cookie"
import { COOKIES, LARAVEL_APP_HOME, Roles, SCREEN_PATHS } from "./constants"
import { Link, useNavigate } from "react-router-dom"
import { useLocation } from "react-router-dom"
import { UserAPI } from "./apis/UserAPI"
import { ADVISOR_APP_HOST } from "./constants"
import { AtticusCircularProgress } from "./views/shared/AtticusCircularProgress"

export function AutoSignup(): JSX.Element {
  const auth0 = useAuth0()
  const [error, setError] = useState<string>("")
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const errorDescription = queryParams.get("error_description")

    if (errorDescription) {
      setError(errorDescription)
      return
    }

    ;(async () => {
      if (!auth0?.user?.email) {
        if (!auth0.isLoading) {
          setError("we didn't find an email for this account")
        }
        return
      }

      try {
        console.log("getting token")
        const token = await auth0.getAccessTokenSilently()
        if (!token) {
          setError("we didn't find a login for this account")
          return
        }
        Cookies.set(COOKIES.authToken, token) // Pulled in by the axios instance

        console.log("getting user from api")
        let myApiUser: User | undefined = await UserAPI.getUserByEmail(auth0.user.email, token)
        if (!myApiUser) {
          // If this user is actually an advisor, redirect them to the advisor site
          console.log("checking to see if user is an advisor")
          myApiUser = await UserAPI.getAdvisorByEmail(auth0.user.email, token)
        }
        if (
          myApiUser?.roleId === Roles.Advisor ||
          myApiUser?.roleId === Roles.Advisor_Admin ||
          myApiUser?.roleId === Roles.Super_Advisor_Admin ||
          myApiUser?.roleId === Roles.Uber_Admin
        ) {
          console.log("Logged-in user is an advisor, redirecting to advisor site")
          window.location.href = ADVISOR_APP_HOST
          return
        }

        if (!myApiUser) {
          // parse signup fields out of the token
          const claims = await auth0.getIdTokenClaims()
          const metadata: any = {}
          const metadataKeys = ["ulp-first-name", "ulp-last-name", "ulp-phone", "ulp-country-id"]
          metadataKeys.map((k) => {
            metadata[k] = claims?.[k]
          })

          // If the user's browser has an invite-id, we need to add it to the metadata
          const inviteId = Cookies.get("AtticusInviteId")

          if (inviteId) {
            metadata["invite-id"] = inviteId
          }

          // console.log("user_metadata", JSON.stringify(metadata, null, 2))
          myApiUser = await UserAPI.handleAutoSignup(auth0.user.email, metadata)
          if (!myApiUser) {
            throw new Error("an unkown error occurred while creating your account")
          }
        }

        if (!myApiUser.hasCompletedOnboarding) {
          navigate(SCREEN_PATHS.onBoarding)
          return
        }

        window.location.href = LARAVEL_APP_HOME
        return
      } catch (e) {
        setError(
          "We were unable to create your account and/or log you in.  The error message we received was '" +
            (e as any).message +
            "'"
        )
      }
    })()
  }, [auth0?.user?.email])

  return error ? (
    <Typography
      variant="body1"
      color="error"
      style={{ margin: "20%", backgroundColor: "white", borderRadius: "1rem", padding: "1rem" }}
    >
      {error}
      <br />
      <br />
      Please contact support@weareatticus.biz for assistance!
      <br />
      <br />
      <Link to={SCREEN_PATHS.signOut}>Sign out</Link>
    </Typography>
  ) : (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
      }}
    >
      <AtticusCircularProgress />
    </Box>
  )
}
